var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getAttributes } from '../../../../../utils';
import { RadioButtonSizes } from '../../../../atoms/radio-button';
import { Container, ContainerDisplays, ContainerFlexDirections, ContainerFlexGaps, ContainerFlexJustifyContents, ContainerFlexWraps, ContainerHeights, ContainerOverflows, ContainerPositions, ContainerPositionSizes, ContainerWidths } from '../../../../templates/container';
import { AddToCart, AddToCartButtonWidths } from '../../common/add-to-cart';
import { AttributeSelector } from '../../common/attribute-selector';
import { ColorSelector, ColorSelectorTypes } from '../../common/color-selector';
import { FlavourProfiles } from '../../common/flavour-profiles';
import { PriceInfo } from '../../common/price-info';
import { ProductChips } from '../../common/product-chips';
import { ProductInfo } from '../../common/product-info';
import { RatingAndReviews } from '../../common/rating-and-reviews';
import { StockInfo } from '../../common/stock-info';
import { ProductCardImage } from './product-card-image';
import { ProductCardName } from './product-card-name';
import { ProductCardShortDescription } from './product-card-short-description';
import { ProductCardChangeEvents } from './types';
// auxiliary
var hasProps = function (props) {
    return props && Boolean(Object.keys(props).length);
};
var checkAttrValuesInVariant = function (state, variant) {
    var _a;
    return (_a = variant === null || variant === void 0 ? void 0 : variant.attributeValues) === null || _a === void 0 ? void 0 : _a.every(function (av) { var _a; return ((_a = state === null || state === void 0 ? void 0 : state.find(function (ps) { return ps.property === av.property; })) === null || _a === void 0 ? void 0 : _a.value) === av.value; });
};
var ImageComponent = memo(function (_a) {
    var _b;
    var imageProps = _a.imageProps, chipsProps = _a.chipsProps, colorSelectorProps = _a.colorSelectorProps;
    var _c = __assign({}, imageProps), alt = _c.alt, linkUrl = _c.linkUrl;
    var colorSelectorConfig = __assign(__assign({}, colorSelectorProps), { sortByStock: false, linkUrl: linkUrl, 'aria-label': alt });
    return (React.createElement(React.Fragment, null,
        (imageProps === null || imageProps === void 0 ? void 0 : imageProps.fallback) ? (React.createElement(Container, { position: ContainerPositions.Relative, overflow: ContainerOverflows.Hidden },
            React.createElement(ProductCardImage, __assign({}, imageProps)),
            Boolean((_b = chipsProps === null || chipsProps === void 0 ? void 0 : chipsProps.chipPropsList) === null || _b === void 0 ? void 0 : _b.length) && (React.createElement(Container, { position: ContainerPositions.Absolute, positionCoordinates: {
                    xs: {
                        top: ContainerPositionSizes.Size_3,
                        left: ContainerPositionSizes.Size_3
                    }
                } },
                React.createElement(ProductChips, __assign({}, chipsProps)))))) : null,
        React.createElement(ColorSelector, __assign({}, colorSelectorConfig, { type: ColorSelectorTypes.Radio }))));
});
var RatingComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) ? React.createElement(RatingAndReviews, __assign({}, props)) : null;
});
var NameComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) ? React.createElement(ProductCardName, __assign({}, props)) : null;
});
var ShortDescriptionComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) ? React.createElement(ProductCardShortDescription, __assign({}, props)) : null;
});
var StockInfoComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) && (props === null || props === void 0 ? void 0 : props.showStockMessage) ? React.createElement(StockInfo, __assign({}, props)) : null;
});
var FlavourProfilesComponent = memo(function (_a) {
    var _b;
    var props = __rest(_a, []);
    return hasProps(props) && ((_b = props === null || props === void 0 ? void 0 : props.profiles) === null || _b === void 0 ? void 0 : _b.length) ? (React.createElement(FlavourProfiles, __assign({}, props))) : null;
});
var PriceInfoComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) ? React.createElement(PriceInfo, __assign({}, props)) : null;
});
var InfoComponent = memo(function (_a) {
    var props = __rest(_a, []);
    return hasProps(props) ? React.createElement(ProductInfo, __assign({}, props)) : null;
});
var AddToCartComponent = memo(function (_a) {
    var props = __rest(_a, []);
    var addToCartProps = __assign({ buttonWidth: AddToCartButtonWidths.Full }, props);
    return hasProps(props) ? React.createElement(AddToCart, __assign({}, addToCartProps)) : null;
});
var AttributeSelectorsComponent = memo(function (_a) {
    var _b, _c;
    var props = __rest(_a, []);
    return hasProps(props) && ((_b = props === null || props === void 0 ? void 0 : props.attributeSelectors) === null || _b === void 0 ? void 0 : _b.length) ? (React.createElement(Container, { flexGap: ContainerFlexGaps.Size_4, display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column }, (_c = props === null || props === void 0 ? void 0 : props.attributeSelectors) === null || _c === void 0 ? void 0 : _c.map(function (attributeSelector) {
        var _a;
        return (React.createElement(AttributeSelector, __assign({ key: attributeSelector.name }, attributeSelector, { onAttributeChange: props === null || props === void 0 ? void 0 : props.onAttributeSelectorsChange((_a = attributeSelector.property) !== null && _a !== void 0 ? _a : '') })));
    }))) : null;
});
var PC = function (_a) {
    var _b, _c, _d, _e;
    var idProp = _a.id, _f = _a.variants, variants = _f === void 0 ? [] : _f, colorSelector = _a.colorSelector, onChangeProp = _a.onChange, rest = __rest(_a, ["id", "variants", "colorSelector", "onChange"]);
    var _g = useState((_b = colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.currentValue) !== null && _b !== void 0 ? _b : null), colorSelected = _g[0], setColorSelected = _g[1];
    var _h = useState((_d = (_c = variants === null || variants === void 0 ? void 0 : variants[0]) === null || _c === void 0 ? void 0 : _c.attributeValues) !== null && _d !== void 0 ? _d : null), attributeSelectorsSelected = _h[0], setAttributeSelectorsSelected = _h[1];
    var currentVariant = useMemo(function () {
        var _a;
        return (__assign({}, ((_a = variants === null || variants === void 0 ? void 0 : variants.find(function (variantData) {
            var colorFilter = colorSelected
                ? variantData.colorValue === colorSelected
                : true;
            var attributeSelectorFilter = attributeSelectorsSelected
                ? attributeSelectorsSelected === null || attributeSelectorsSelected === void 0 ? void 0 : attributeSelectorsSelected.every(function (attributeSelected) {
                    var _a, _b;
                    return ((_b = (_a = variantData.attributeValues) === null || _a === void 0 ? void 0 : _a.find(
                    // eslint-disable-next-line max-nested-callbacks
                    function (attributeVariant) {
                        return attributeVariant.property === attributeSelected.property;
                    })) === null || _b === void 0 ? void 0 : _b.value) === attributeSelected.value;
                })
                : true;
            return colorFilter && attributeSelectorFilter;
        })) !== null && _a !== void 0 ? _a : variants[0])));
    }, [attributeSelectorsSelected, colorSelected, variants]);
    var productContainer = __assign({ display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexWrap: ContainerFlexWraps.NoWrap, flexJustifyContent: ContainerFlexJustifyContents.Start, flexGap: ContainerFlexGaps.Size_4, width: ContainerWidths.Boxed, id: idProp }, getAttributes(rest));
    var imageData = currentVariant.imageData, productChips = currentVariant.productChips, nameData = currentVariant.nameData, shortDescriptionData = currentVariant.shortDescriptionData, stockInfo = currentVariant.stockInfo, priceInfo = currentVariant.priceInfo, flavourProfiles = currentVariant.flavourProfiles, productInfo = currentVariant.productInfo, rating = currentVariant.rating, attributeSelectors = currentVariant.attributeSelectors, attributeValues = currentVariant.attributeValues, addToCart = currentVariant.addToCart;
    var onColorChange = useCallback(function (e, colorVariant) {
        var _a;
        var newColorSelected = (_a = colorVariant.value) !== null && _a !== void 0 ? _a : null;
        setColorSelected(newColorSelected);
        setAttributeSelectorsSelected(function (prevState) {
            var _a, _b, _c, _d;
            var newColorVariants = (_a = variants === null || variants === void 0 ? void 0 : variants.filter(function (variantData) { return variantData.colorValue === newColorSelected; })) !== null && _a !== void 0 ? _a : [];
            var variant = newColorVariants.find(function (v) {
                return checkAttrValuesInVariant(prevState, v);
            });
            var state = (_d = (_b = variant === null || variant === void 0 ? void 0 : variant.attributeValues) !== null && _b !== void 0 ? _b : (_c = newColorVariants[0]) === null || _c === void 0 ? void 0 : _c.attributeValues) !== null && _d !== void 0 ? _d : null;
            onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, {
                type: ProductCardChangeEvents.Color,
                value: newColorSelected || ''
            }, variant !== null && variant !== void 0 ? variant : newColorVariants[0]);
            return state;
        });
    }, [onChangeProp, variants]);
    var onAttributeSelectorsChange = useCallback(function (property) {
        return function (e, attributeVariant) {
            var _a;
            var findAttrProperty = function (attrValues) {
                return attrValues === null || attrValues === void 0 ? void 0 : attrValues.find(function (state) { return state.property === property; });
            };
            var newAttrVariants = (_a = variants === null || variants === void 0 ? void 0 : variants.filter(function (v) {
                var _a;
                return (!colorSelected || v.colorValue === colorSelected) &&
                    ((_a = findAttrProperty(v === null || v === void 0 ? void 0 : v.attributeValues)) === null || _a === void 0 ? void 0 : _a.value) ===
                        attributeVariant.value;
            })) !== null && _a !== void 0 ? _a : [];
            setAttributeSelectorsSelected(function (prevState) {
                var _a, _b, _c, _d;
                var auxState = __spreadArray([], (prevState || []), true);
                var auxIndex = auxState.findIndex(function (state) { return state.property === property; });
                if (auxIndex !== -1) {
                    auxState[auxIndex] = __assign(__assign({}, auxState[auxIndex]), { value: (_a = attributeVariant.value) !== null && _a !== void 0 ? _a : '' });
                }
                var variant = (_b = newAttrVariants.find(function (v) {
                    return checkAttrValuesInVariant(auxState, v);
                })) !== null && _b !== void 0 ? _b : newAttrVariants[0];
                var state = (_c = variant === null || variant === void 0 ? void 0 : variant.attributeValues) !== null && _c !== void 0 ? _c : null;
                onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, {
                    type: ProductCardChangeEvents.Attribute,
                    value: (_d = findAttrProperty(state)) !== null && _d !== void 0 ? _d : null
                }, variant);
                return state;
            });
        };
    }, [colorSelected, onChangeProp, variants]);
    var onQTYChange = useCallback(function (e) {
        var _a;
        (_a = addToCart === null || addToCart === void 0 ? void 0 : addToCart.onQTYChange) === null || _a === void 0 ? void 0 : _a.call(addToCart, e);
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, {
            type: ProductCardChangeEvents.Selector,
            value: e.target.value
        }, currentVariant);
    }, [addToCart, currentVariant, onChangeProp]);
    var imageProps = useMemo(function () {
        var _a, _b;
        var _c = __assign({}, imageData), _d = _c.alt, alt = _d === void 0 ? (_a = nameData === null || nameData === void 0 ? void 0 : nameData.productName) !== null && _a !== void 0 ? _a : '' : _d, imageRest = __rest(_c, ["alt"]);
        return {
            imageProps: __assign({ alt: alt }, imageRest),
            chipsProps: productChips,
            colorSelectorProps: __assign(__assign({}, colorSelector), { currentValue: colorSelected, colorVariants: (_b = colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.colorVariants) === null || _b === void 0 ? void 0 : _b.map(function (cv) {
                    var _a;
                    return (__assign(__assign({}, cv), { stock: cv.value === colorSelected
                            ? (_a = currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.stockInfo) === null || _a === void 0 ? void 0 : _a.stockStatus
                            : cv.stock }));
                }), size: (colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.size) || RadioButtonSizes.Size_4, onChange: onColorChange })
        };
    }, [
        nameData === null || nameData === void 0 ? void 0 : nameData.productName,
        imageData,
        productChips,
        colorSelector,
        colorSelected,
        onColorChange,
        (_e = currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.stockInfo) === null || _e === void 0 ? void 0 : _e.stockStatus
    ]);
    useEffect(function () {
        var _a;
        setColorSelected((_a = colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.currentValue) !== null && _a !== void 0 ? _a : null);
    }, [colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.currentValue]);
    useEffect(function () {
        setAttributeSelectorsSelected(attributeValues !== null && attributeValues !== void 0 ? attributeValues : null);
    }, [attributeValues]);
    return (React.createElement(Container, __assign({}, productContainer, { minHeight: ContainerHeights.Full }),
        React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexGap: ContainerFlexGaps.Size_3 },
            React.createElement(ImageComponent, __assign({}, imageProps)),
            React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexGap: ContainerFlexGaps.Size_4 },
                React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexGap: ContainerFlexGaps.Size_2 },
                    React.createElement(NameComponent, __assign({}, nameData)),
                    React.createElement(RatingComponent, __assign({}, rating)),
                    React.createElement(ShortDescriptionComponent, __assign({}, shortDescriptionData))),
                React.createElement(StockInfoComponent, __assign({}, stockInfo)),
                React.createElement(PriceInfoComponent, __assign({}, priceInfo)),
                React.createElement(FlavourProfilesComponent, __assign({}, flavourProfiles)),
                React.createElement(AttributeSelectorsComponent, { attributeSelectors: attributeSelectors, onAttributeSelectorsChange: onAttributeSelectorsChange }),
                React.createElement(InfoComponent, __assign({}, productInfo)))),
        React.createElement("div", { style: { marginTop: 'auto' } },
            React.createElement(AddToCartComponent, __assign({}, addToCart, { onQTYChange: onQTYChange })))));
};
export { PC as ProductCard };
